import { Action, ActionType } from "../../../AppDataProvider/actions";
import { AppContext, BookingData } from "../../../AppDataProvider/AppDataProvider";
import { GetServiceResourcesForBookingToolQuery, ResourceSelectionMode, useGetServiceResourcesForBookingToolQuery } from "../../../GeneratedGraphQL";
import React, { useContext, useEffect } from "react";
import { absoluteCentre } from "../../../common/styles";
import { LoadingPill as BaseLoadingPill } from "../../../common/components";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { PartRequired } from "../../../utils/types";
import { requireBookingDataFields } from "../../../utils";
import { StepContent } from "../StepContent/StepContent";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

/**
 * The data layer used for loading resources for a
 * specific booking tool and service.
 */
export function ResourceData() {
  const {
    t
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  requireBookingDataFields<PartRequired<BookingData, "organisation" | "service">>(appData.bookingData, ["service", "organisation"]);
  const {
    organisation,
    service,
    resourceIds
  } = appData.bookingData;
  const resourceSelectionBeforeAvailability = organisation.resourceSelectionBeforeAvailability;
  const bookingToolId = appData.bookingTool._id;
  const {
    data,
    loading,
    error,
    fetchMore
  } = useGetServiceResourcesForBookingToolQuery({
    fetchPolicy: "network-only",
    variables: {
      bookingToolId,
      id: service._id,
      ...(!resourceSelectionBeforeAvailability && resourceIds && {
        resourceIds
      })
    }
  });
  useEffect(() => {
    // All the error dialogs for this page are the same,
    // except for their exact error content.
    // Ignoring onClick coverage because it's not practical to test, and it
    // would be redundant to test a native function anyway.
    const showModalDialog = (content: string): Action => ({
      payload: {
        buttonLabel: t("tryAgain"),
        content,
        icon: faExclamationTriangle,
        onClick: /* istanbul ignore next */() => location.reload(),
        title: t("serverError")
      },
      type: ActionType.SHOW_MODAL_DIALOG
    });
    if (!loading) {
      if (error) {
        console.error({
          error
        });
        appDataDispatch(showModalDialog(t("errors.generic")));
      } else if (!data || !data.service) {
        appDataDispatch(showModalDialog(t("errors.noServiceFound")));
      } else if (!data.service.resourcesForBookingTool.edges.length) {
        appDataDispatch(showModalDialog(t("errors.noResources")));
      }
      // If we're in resource group selection mode, but there are no resource
      // groups amongst the returned resources, throw an error.
      if (appData.bookingTool.resourceSelectionMode === ResourceSelectionMode.Group && !isAtLeastOneResourceGroup(data)) {
        appDataDispatch(showModalDialog(t("errors.noResourceGroups")));
      }
    }
  }, [appDataDispatch, data, error, loading, t, appData.bookingTool.resourceSelectionMode]);
  if (loading) return <LoadingPill />;
  if (error || !data || !data.service) return null;
  const resources = data.service.resourcesForBookingTool;
  if (!resources.edges.length) return null;
  if (data.service.resourcesForBookingTool.pageInfo.hasNextPage) {
    void fetchMore({
      variables: {
        bookingToolId,
        cursor: resources.pageInfo.endCursor,
        id: service._id
      }
    });
    return <LoadingPill />;
  }
  return <StepContent resources={resources} data-sentry-element="StepContent" data-sentry-component="ResourceData" data-sentry-source-file="ResourceData.tsx" />;
}
const LoadingPill = styled(BaseLoadingPill)`
  ${absoluteCentre}
`;
function isAtLeastOneResourceGroup(data: GetServiceResourcesForBookingToolQuery | undefined) {
  return data?.service?.resourcesForBookingTool.edges && !data.service.resourcesForBookingTool.pageInfo.hasNextPage && data.service.resourcesForBookingTool.edges.flatMap(edge => edge.node.groups.edges).length > 0;
}
export default ResourceData;